import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { t } from '@sm/intl';
import { Banner } from '@wds/banner';
import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';
import SurveyPreview from './SurveyPreview';
import AcceptRejectChangesBanner from './AcceptRejectChangesBanner';
import PromptInputBox from './PromptInputBox';
import SurveyUpdatedBanner from './SurveyUpdatedBanner';
import useBuildWithAIRevision from './useBuildWithAIRevision/useBuildWithAIRevision';
import removeBuildWithAISurveyTypenames from './util/removeBuildWithAISurveyTypenames';
import calculateMinDiff from './util/calculateMinimumDiff';
const COPY = {
  PreviewPageHeading: {
    id: 'buildWithAI.ReviseSurvey.PreviewPageHeading',
    defaultMessage: 'Preview and make changes',
    description: '[Type: label][Vis: high] - Page title for AI survey revision page'
  },
  PreviewPageSubHeading: {
    id: 'buildWithAI.ReviseSurvey.PreviewPageSubHeading',
    defaultMessage: 'See questions and answers. Make changes with AI.',
    description: '[Type: label][Vis: high] - Page description for AI survey revision page'
  },
  PromptInputHeader: {
    id: 'buildWithAI.ReviseSurvey.PromptInputHeader',
    defaultMessage: 'Make changes with prompts',
    description: '[Type: label][Vis: high] - Description for AI prompt input section of page'
  },
  SystemErrorMessage: {
    id: 'buildWithAI.ReviseSurvey.SystemErrorMessage',
    defaultMessage: "We've experienced an error. Please reload the page or try again later.",
    description: '[Type: label][Vis: high] - Error message for general system failure'
  }
};
const useStyles = createUseStyles(theme => ({
  surveyPreviewContainer: {
    border: theme.border.muted,
    borderRadius: theme.radius.small,
    borderWidth: 2
  }
}));
/**
 * Component for survey revision in Build with AI.
 *
 * Allows the user to input prompts for changing/refining the generated survey
 * using AI.
 */
const ReviseSurvey = ({
  onFinished,
  onCancel,
  initialRemainingPrompts,
  survey: initialSurvey,
  initialPrompt
}) => {
  const {
    surveyPreviewContainer
  } = useStyles();
  const [promptsRemaining, setPromptsRemaining] = useState(initialRemainingPrompts);
  const [surveyHistory, setSurveyHistory] = useState([initialSurvey]);
  const [revisionState, setRevisionState] = useState({
    variant: 'empty'
  });
  const [promptHistory, setPromptHistory] = useState([initialPrompt]);
  const currentSurvey = surveyHistory[surveyHistory.length - 1];
  const [triggerSurveyRevision, surveyRevisionState] = useBuildWithAIRevision({
    onSurveyRevision: ({
      survey: proposedSurvey,
      remainingCnt
    }) => {
      setRevisionState({
        variant: 'proposed',
        survey: proposedSurvey,
        difference: calculateMinDiff(removeBuildWithAISurveyTypenames(currentSurvey).questions, removeBuildWithAISurveyTypenames(proposedSurvey).questions)
      });
      setPromptsRemaining(remainingCnt);
    }
  });
  const onPrompt = prompt => {
    triggerSurveyRevision(prompt, promptHistory, removeBuildWithAISurveyTypenames(currentSurvey));
    setPromptHistory(prevPromptHistory => [...prevPromptHistory, prompt]);
    setRevisionState({
      variant: 'empty'
    });
  };
  const onAcceptChanges = () => {
    if (revisionState.variant !== 'proposed') {
      return;
    }
    const prevRevisionState = revisionState;
    setRevisionState({
      variant: 'accepted',
      survey: prevRevisionState.survey,
      difference: prevRevisionState.difference
    });
    setSurveyHistory(prevSurveyHistory => [...prevSurveyHistory, prevRevisionState.survey]);
  };
  const onRejectChanges = () => {
    setRevisionState({
      variant: 'rejected'
    });
  };
  const hasError = surveyRevisionState.state === 'error';
  return _jsx(Box, {
    display: "flex",
    flexDirection: "column",
    flexWrap: false,
    justifyContent: "flex-start",
    alignItems: "stretch",
    children: _jsxs(Grid, {
      noBleeds: true,
      children: [hasError && _jsx(Grid.Item, {
        md: 12,
        children: _jsx(Box, {
          mb: 4,
          children: _jsx(Banner, {
            color: "error",
            showIcon: true,
            children: t(COPY.SystemErrorMessage)
          })
        })
      }), _jsxs(Grid.Item, {
        md: 12,
        children: [_jsxs(Box, {
          mb: 6,
          children: [_jsx(Box, {
            mb: 2,
            children: _jsx(Typography, {
              variant: "pageTitle",
              component: "h1",
              weight: "medium",
              children: t(COPY.PreviewPageHeading)
            })
          }), _jsx(Typography, {
            variant: "bodySm",
            component: "p",
            children: t(COPY.PreviewPageSubHeading)
          })]
        }), _jsxs(Box, {
          mb: 5,
          children: [_jsx(AcceptRejectChangesBanner, {
            onAcceptChanges: onAcceptChanges,
            onRejectChanges: onRejectChanges,
            prompt: promptHistory[promptHistory.length - 1],
            show: revisionState.variant === 'proposed'
          }), _jsx(SurveyUpdatedBanner, {
            show: revisionState.variant === 'accepted',
            prompt: promptHistory[promptHistory.length - 1]
          })]
        })]
      }), _jsx(Grid.Item, {
        sm: 12,
        md: 7,
        children: _jsx(Box, {
          className: surveyPreviewContainer,
          p: 5,
          element: "section",
          children: _jsx(SurveyPreview, {
            survey: currentSurvey,
            revisionState: revisionState
          })
        })
      }), _jsx(Grid.Item, {
        sm: 12,
        md: 5,
        children: _jsx(PromptInputBox, {
          disabled: surveyRevisionState.state === 'loading',
          loading: surveyRevisionState.state === 'loading',
          onFinished: onFinished,
          onCancel: onCancel,
          onPrompt: onPrompt,
          promptsRemaining: promptsRemaining
        })
      })]
    })
  });
};
export default ReviseSurvey;